import { Autocomplete, Button, ButtonGroup, TextField } from '@mui/material'
import dayjs from 'dayjs'
import { useAuth } from 'hooks'
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react'
import React, { useEffect, useMemo } from 'react'
import { funcionario as funcionario_api } from 'service/api'
import { useStore } from 'store'
import { filterLocaisCentro } from './filter-select'
import { SelectProps } from './types'

interface IProps extends SelectProps {
    ativos?: boolean
    centroCustoId?: number
    localId?: number
    children?: never
}

export function SelectFuncionario({
    size = 'small',
    width = 260,
    button: hasButtons = false,
    disabled,
    ativos,
    centroCustoId,
    localId,
    variant = 'filled',
}: IProps) {
    const {
        decoded: { categoria },
    } = useAuth()
    const company = useStore(state => state.company)
    const funcionario = useStore(
        state =>
            state.employee ?? {
                id: -1,
            },
    )
    const empresaId = useStore(state => state.companyId)
    const setEmployeeId = useStore(state => state.setEmployeeId)
    const selectedDate = useStore(state => state.selectedDate)

    const { data: funcionarios = [] } = funcionario_api.useFetch({
        queryParams: { empresaId },
        enabled: empresaId > 0,
    })

    const data = React.useMemo(
        () =>
            funcionarios.filter(obj => {
                if (ativos && obj.dtRescisao) {
                    const tolerancia = company?.diasRescisao ?? 0
                    return dayjs(obj.dtRescisao).isAfter(
                        dayjs(selectedDate).subtract(tolerancia, 'day'),
                    )
                }

                return filterLocaisCentro<typeof obj>(
                    !!localId && localId > 0 ? [localId] : [],
                    [centroCustoId!],
                    it => it,
                )(obj)
            }),
        [funcionarios, ativos, centroCustoId, localId, selectedDate],
    )

    const isPrimeiroFuncionario = useMemo(() => {
        if (data && data.length > 0) {
            return funcionario.id === data[0].id
        }
        return false
    }, [data, funcionario.id])

    const isUltimoFuncionario = useMemo(() => {
        if (data && data.length > 0) {
            return funcionario.id === data[data.length - 1].id
        }
        return false
    }, [data, funcionario.id])

    async function next() {
        data?.forEach((item, index) => {
            if (item.id === funcionario.id) {
                setEmployeeId(data[index + 1]?.id)
            }
        })
    }

    async function previous() {
        data?.forEach((item, index) => {
            if (item.id === funcionario.id) {
                setEmployeeId(data[index - 1]?.id)
            }
        })
    }

    const selectedFuncionario = data.find(f => f.id === funcionario.id) || null

    // Set default employee if none selected or current not in list
    useEffect(() => {
        if (funcionario.id === -1 && data.length > 0) {
            setEmployeeId(data[0].id)
        } else if (
            funcionario.id !== -1 &&
            !data.find(f => f.id === funcionario.id)
        ) {
            setEmployeeId(data.at(0)?.id || -1)
        }
    }, [data])

    return (
        <div className="flex items-center gap-2" key={selectedFuncionario?.id}>
            <Autocomplete
                className="flex-1"
                style={{ width }}
                options={data}
                getOptionLabel={option => option?.nome || ''}
                value={selectedFuncionario}
                onChange={(_, value) => {
                    if (value) setEmployeeId(value.id)
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                disabled={disabled || categoria === 5}
                renderInput={params => (
                    <TextField
                        {...params}
                        label="Funcionário"
                        size={size}
                        variant={variant}
                        className="w-full truncate"
                        InputLabelProps={{ shrink: true }}
                        placeholder="Selecione um funcionário"
                    />
                )}
                noOptionsText={
                    <span className="text-gray-500 select-none">
                        Nenhum funcionário
                    </span>
                }
                clearIcon={null}
            />
            {hasButtons && categoria !== 5 && (
                <ButtonGroup variant="outlined" className="h-full">
                    <Button
                        onClick={previous}
                        disabled={isPrimeiroFuncionario}
                        className="w-12 min-w-0 px-0"
                    >
                        <ChevronLeftIcon />
                    </Button>
                    <Button
                        onClick={next}
                        disabled={isUltimoFuncionario}
                        className="w-12 min-w-0 px-0"
                    >
                        <ChevronRightIcon />
                    </Button>
                </ButtonGroup>
            )}
        </div>
    )
}
